import { FORBIDDEN } from 'http-status-codes';

import fetchRecommendations from 'src/api/fetch-recommendations';
import fetchAppliedOffers from 'src/api/fetch-applied-offers';
import fetchSavedOffers from 'src/api/fetch-saved-offers';
import fetchStatusTranslations from 'src/api/fetch-status-translations';
import fetchFavouriteStatus from 'src/api/fetch-favourite-status';
import combineOffersWithFavourites from 'src/api/combine-offers-with-favourites';

import { setStatuses } from 'src/redux/actions/translations';


const SET_LAST_SEEN_OFFERS = 'SET_LAST_SEEN_OFFERS';
const SET_SAVED_OFFERS = 'SET_SAVED_OFFERS';
const SET_APPLIED_OFFERS = 'SET_APPLIED_OFFERS';
const REMOVE_LAST_SEEN_OFFER = 'REMOVE_LAST_SEEN_OFFER';
const TOGGLE_LAST_SEEN_OFFER_FAVOURITE_STATUS = 'TOGGLE_LAST_SEEN_OFFER_FAVOURITE_STATUS';
const TOGGLE_SAVED_OFFER_FAVOURITE_STATUS = 'TOGGLE_SAVED_OFFER_FAVOURITE_STATUS';
const SET_AGREEMENT = 'SET_AGREEMENT';

const setLastSeenOffers = lastSeenOffers => ({
  type: SET_LAST_SEEN_OFFERS,
  payload: {
    lastSeenOffers
  }
});

const setSavedOffers = savedOffers => ({
  type: SET_SAVED_OFFERS,
  payload: {
    savedOffers
  }
});

const setAppliedOffers = appliedOffers => ({
  type: SET_APPLIED_OFFERS,
  payload: {
    appliedOffers
  }
});

const toggleLastSeenOfferFavouriteStatus = ({ offerId, isFavourite }) => ({
  type: TOGGLE_LAST_SEEN_OFFER_FAVOURITE_STATUS,
  payload: {
    offerId,
    isFavourite
  }
});

const toggleSavedOfferFavouriteStatus = ({ offerId, isFavourite }) => ({
  type: TOGGLE_SAVED_OFFER_FAVOURITE_STATUS,
  payload: {
    offerId,
    isFavourite
  }
});

const removeLastSeenOffer = offerId => ({
  type: REMOVE_LAST_SEEN_OFFER,
  payload: {
    offerId
  }
});

const setProfilingAgreement = hasProfilingAgreement => ({
  type: SET_AGREEMENT,
  payload: {
    hasProfilingAgreement
  }
});

const getRecommendations = () => async (dispatch, getState, { http }) => {
  const {
    contextReducer: { context },
    credentialsReducer: { mpUserId, identityId }
  } = getState();

  const [offers, fetchError] = await fetchRecommendations({
    http,
    params: {
      context,
      mpUserId: mpUserId || null,
      identityId: identityId || null
    }
  })
    .then(data => [data, null])
    .catch(error => [[], error]);

  if (
    fetchError
      && fetchError.response
      && fetchError.response.status === FORBIDDEN
  ) {
    dispatch(setProfilingAgreement(false));
  } else {
    dispatch(setProfilingAgreement(true));
  }

  return offers;
};

const getFavouritesStatus = offers => async (dispatch, getState, { http }) => {
  const offerIds = offers.reduce(
    (params, { offerId }) => !params.append('offerIds', offerId) && params,
    // eslint-disable-next-line compat/compat
    new URLSearchParams()
  );

  const [statuses] = await fetchFavouriteStatus({
    http,
    params: offerIds
  })
    .then(data => [data, null])
    .catch(error => [[], error]);

  return statuses;
};

const getLastSeenOffers = () => async (dispatch, getState) => {
  const {
    credentialsReducer: { mpUserId }
  } = getState();

  const offers = await dispatch(getRecommendations());

  if (!mpUserId || offers.length === 0) {
    dispatch(setLastSeenOffers(offers));
    return;
  }

  const favourites = await dispatch(getFavouritesStatus(offers));
  dispatch(
    setLastSeenOffers(combineOffersWithFavourites({ offers, favourites }))
  );
};

const getSavedOffers = () => async (dispatch, getState, { http }) => {
  const {
    credentialsReducer: { mpUserId }
  } = getState();

  if (!mpUserId) {
    dispatch(setSavedOffers([]));
    return;
  }

  const [offers] = await fetchSavedOffers({ http })
    .then(data => [data, null])
    .catch(error => [[], error]);

  dispatch(setSavedOffers(offers));
};

const getStatusTranslations = () => async (dispatch, getState, { http }) => {
  const {
    translationsReducer: { statuses }
  } = getState();

  if (!(statuses && Object.values(statuses).length > 0)) {
    const [newStatuses] = await fetchStatusTranslations({ http })
      .then(data => [data, null])
      .catch(error => [{}, error]);

    dispatch(setStatuses(newStatuses));
    return newStatuses;
  }
  return statuses;
};

const getAppliedOffers = () => async (dispatch, getState, { http }) => {
  const {
    credentialsReducer: { mpUserId }
  } = getState();

  if (!mpUserId) {
    dispatch(setAppliedOffers([]));
    return;
  }

  const [offers] = await fetchAppliedOffers({ http })
    .then(data => [data, null])
    .catch(error => [[], error]);

  if (offers.length === 0) {
    dispatch(setAppliedOffers([]));
    return;
  }

  const statusTranslations = await dispatch(getStatusTranslations());

  dispatch(
    setAppliedOffers(
      offers.map(offer => ({
        ...offer,
        statusTranslation: statusTranslations[offer.actualStatus]
      }))
    )
  );
};

export {
  SET_LAST_SEEN_OFFERS,
  SET_SAVED_OFFERS,
  SET_APPLIED_OFFERS,
  REMOVE_LAST_SEEN_OFFER,
  TOGGLE_LAST_SEEN_OFFER_FAVOURITE_STATUS,
  TOGGLE_SAVED_OFFER_FAVOURITE_STATUS,
  SET_AGREEMENT,
  setLastSeenOffers,
  setSavedOffers,
  setAppliedOffers,
  toggleLastSeenOfferFavouriteStatus,
  toggleSavedOfferFavouriteStatus,
  removeLastSeenOffer,
  setProfilingAgreement,
  getLastSeenOffers,
  getSavedOffers,
  getAppliedOffers
};
